import { fmtDate, httpUrl } from '@jt/core';
import type { ApplicationFull, InboxEvent } from '@jt/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, get, post } from '../api/client';
import { applyFull, keys, refreshSummaries } from '../api/hooks';
import { useUI } from '../components/overlays';
import { Icon } from '../components/ui';
import { ParsedPreview, PasteForm } from '../drafts/PasteForm';
import { integrationsKey, useIntegrations } from '../pages/integrations';

export const inboxKey = ['inbox'] as const;

export function useInbox(poll = false) {
  return useQuery({
    queryKey: inboxKey,
    queryFn: () => get<InboxEvent[]>('/inbox'),
    refetchInterval: poll ? 3000 : false,
  });
}

const when = (iso: string | null | undefined) =>
  iso ? `${fmtDate(iso.slice(0, 10))}, ${iso.slice(11, 16)} UTC` : '';

function EventRow({ e }: { e: InboxEvent }) {
  const ui = useUI();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const done = async (full?: ApplicationFull) => {
    if (full) applyFull(qc, full);
    await qc.invalidateQueries({ queryKey: inboxKey });
    void qc.invalidateQueries({ queryKey: keys.apps() });
    refreshSummaries(qc);
  };
  const apply = async () => {
    setBusy(true);
    try {
      await done(await post<ApplicationFull>(`/inbox/${e.id}/apply`, {}));
      ui.toast('Применено');
    } catch (x) {
      ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  };
  const edit = () =>
    ui.openSheet({
      title: `Письмо: ${e.subject || 'без темы'}`,
      body: (
        <>
          <section className="sec">
            <div className="meta">
              {e.sender}, {fmtDate(e.occurredOn)}
            </div>
            {e.externalUrl && (
              <div style={{ marginTop: 8 }}>
                <a className="btn sm" href={httpUrl(e.externalUrl)} target="_blank" rel="noopener noreferrer">
                  Открыть в Gmail
                </a>
              </div>
            )}
            {e.raw && (
              <details className="raw">
                <summary>Текст письма</summary>
                <pre>{e.raw}</pre>
              </details>
            )}
          </section>
          <ParsedPreview
            r={{ parsed: e.data, raw: e.raw, truncated: false }}
            targetId={e.targetApplicationId}
            submit={async (body) => {
              const full = await post<ApplicationFull>(`/inbox/${e.id}/apply`, { edits: body });
              await done(full);
              return full;
            }}
          />
        </>
      ),
    });
  const pasteText = () =>
    ui.openSheet({
      title: 'Вставить сообщение из LinkedIn',
      body: (
        <>
          <p className="muted sm" style={{ margin: '0 0 10px' }}>
            Скопируйте переписку в LinkedIn и вставьте сюда — Claude найдёт вакансию и статус.
          </p>
          <PasteForm
            targetId={e.targetApplicationId}
            submit={async (body) => {
              const full = await post<ApplicationFull>(`/inbox/${e.id}/apply`, { edits: body });
              await done(full);
              return full;
            }}
          />
        </>
      ),
    });
  const skip = async () => {
    await post(`/inbox/${e.id}/skip`);
    await done();
  };
  if (e.textInLinkedIn)
    return (
      <div className="lrow">
        <span className="tic v">
          <Icon name="linkedin" size={20} />
        </span>
        <div className="lm">
          <b>{e.title}</b>
          <div className="meta">
            {e.raw} · {fmtDate(e.occurredOn)}
          </div>
          <div className="txt">
            Текста в письме нет — откройте переписку, скопируйте её и нажмите «Вставить текст».
          </div>
          <div className="btnrow">
            {e.externalUrl && (
              <a
                className="btn sm primary"
                href={httpUrl(e.externalUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon name="ext" size={15} /> Открыть в LinkedIn
              </a>
            )}
            <button className="btn sm" onClick={pasteText}>
              Вставить текст
            </button>
            {e.targetApplicationId && (
              <button className="btn sm" onClick={apply} disabled={busy}>
                Отметить в истории
              </button>
            )}
            <button className="btn sm ghost" onClick={skip}>
              Скрыть
            </button>
          </div>
        </div>
      </div>
    );
  return (
    <div className="lrow">
      <span className="tic v">
        <Icon name={e.source === 'gmail' ? 'mail' : 'send'} size={20} />
      </span>
      <div className="lm">
        <b>{e.title}</b>
        <div className="meta">
          {e.sender} · {fmtDate(e.occurredOn)}
        </div>
        <div className="txt">{e.summary}</div>
        {e.nextStep && (
          <div className="meta">
            Дальше: {e.nextStep}
            {e.nextDate ? `, ${fmtDate(e.nextDate)}` : ''}
          </div>
        )}
        <div className="btnrow">
          <button className="btn sm primary" onClick={apply} disabled={busy}>
            Применить
          </button>
          <button className="btn sm" onClick={edit}>
            Изменить
          </button>
          <button className="btn sm ghost" onClick={skip}>
            Пропустить
          </button>
          {e.externalUrl && (
            <a
              className="btn sm ghost"
              href={httpUrl(e.externalUrl)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="ext" size={15} /> Gmail
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Блок Gmail на экране «Сообщения»: проверка, статус, события к подтверждению. */
export function InboxBlock() {
  const ui = useUI();
  const qc = useQueryClient();
  const integ = useIntegrations();
  const [scanning, setScanning] = useState(false);
  const inbox = useInbox(scanning);
  const startedAt = useRef<string | null>(null);
  const g = integ.data?.gmail;
  const job = integ.data?.jobs.find((j) => j.name === 'gmail-scan');

  // пока идёт проверка — обновляем статус; как только задача завершилась, показываем итог
  useEffect(() => {
    if (!scanning) return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: integrationsKey }), 3000);
    const stop = setTimeout(() => setScanning(false), 180_000);
    return () => {
      clearInterval(t);
      clearTimeout(stop);
    };
  }, [scanning, qc]);
  // проверка закончилась — сменилось время последней проверки или появилась ошибка
  useEffect(() => {
    if (!scanning || !g || startedAt.current === null) return;
    const mark = `${g.lastScanAt ?? ''}|${g.lastError ?? ''}`;
    if (mark === startedAt.current) return;
    setScanning(false);
    void qc.invalidateQueries({ queryKey: inboxKey });
    refreshSummaries(qc);
    ui.toast(
      g.lastError
        ? `Проверка не удалась: ${g.lastError}`
        : `Проверка завершена${job?.message ? `: ${job.message}` : ''}`,
    );
  }, [scanning, g, job, qc, ui]);

  const scan = async () => {
    try {
      startedAt.current = `${g?.lastScanAt ?? ''}|${g?.lastError ?? ''}`;
      await post('/integrations/gmail/scan');
      setScanning(true);
    } catch (x) {
      ui.toast(x instanceof ApiError ? x.message : 'Не получилось');
    }
  };

  const P = inbox.data ?? [];
  // сообщения LinkedIn без вакансии «Применить все» пропускает
  const applicable = P.filter((e) => !e.textInLinkedIn || e.targetApplicationId).length;
  const pending = P.length > 0 && (
    <section className="box">
      <h2>Ждут подтверждения · {P.length}</h2>
      <Pending events={P} />
    </section>
  );
  if (!g?.connected)
    return (
      <>
        <section className="box">
          <h2>Почта Gmail</h2>
          <p className="muted" style={{ margin: 0 }}>
            Gmail не подключён. Подключите его в <Link to="/settings#gmail">«Настройках»</Link> — письма по
            откликам будут разбираться сами. Или разбирайте письма через «Вставить сообщение».
          </p>
          {applicable > 1 && (
            <div className="btnrow" style={{ marginTop: 10 }}>
              <ApplyAll count={applicable} />
            </div>
          )}
        </section>
        {pending}
      </>
    );
  return (
    <>
      <section className="box">
        <div className="boxh">
          <h2>Почта Gmail</h2>
          <span className="muted sm">
            {g.lastScanAt ? `проверено ${when(g.lastScanAt)}` : 'ещё не проверялась'}
          </span>
        </div>
        <div className="btnrow">
          <button className="btn primary" onClick={scan} disabled={scanning || g.needsReauth}>
            <Icon name="mail" /> {scanning ? 'Проверяю…' : 'Проверить сейчас'}
          </button>
          {applicable > 1 && <ApplyAll count={applicable} />}
        </div>
        <p className="muted sm" role="status" style={{ margin: '10px 0 0' }}>
          {g.needsReauth ? (
            <span style={{ color: 'var(--danger)' }}>
              Доступ к Gmail истёк — <Link to="/settings#gmail">переподключите почту</Link>.
            </span>
          ) : g.lastError ? (
            `Последняя проверка не удалась: ${g.lastError}`
          ) : scanning ? (
            'Ищу письма и разбираю их с Claude — это может занять минуту…'
          ) : (
            (job?.message ?? '')
          )}
        </p>
      </section>
      {pending}
    </>
  );
}

function Pending({ events }: { events: InboxEvent[] }) {
  return (
    <div className="list">
      {events.map((e) => (
        <EventRow key={e.id} e={e} />
      ))}
    </div>
  );
}

function ApplyAll({ count }: { count: number }) {
  const ui = useUI();
  const qc = useQueryClient();
  return (
    <button
      className="btn"
      onClick={async () => {
        const r = await post<{ count: number }>('/inbox/apply-all');
        await qc.invalidateQueries({ queryKey: inboxKey });
        void qc.invalidateQueries({ queryKey: keys.apps() });
        refreshSummaries(qc);
        ui.toast(`Применено событий: ${r.count}`);
      }}
    >
      Применить все · {count}
    </button>
  );
}
